import React, { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import storage from "./lib/storage";
import { bootSync } from "./lib/sync";
import { ProProvider, useProContext } from "./lib/proContext";
import { track, EVENTS } from "./lib/analytics";
import notifications from "./lib/notifications";
import InstallPrompt from "./components/InstallPrompt";
import OfflineBadge from "./components/OfflineBadge";
import { SkeletonList } from "./components/Skeleton";

// Code-split every route to keep the initial bundle small. The home/scan
// route loads first; everything else (Auth, Photo OCR, Pro flows, etc.)
// downloads lazily on first navigation.
const Onboarding      = lazy(() => import("./pages/Onboarding"));
const Home            = lazy(() => import("./pages/Home"));
const Scan            = lazy(() => import("./pages/Scan"));
const ScanEntry       = lazy(() => import("./pages/ScanEntry"));
const ManualEntry     = lazy(() => import("./pages/ManualEntry"));
const ProductResult   = lazy(() => import("./pages/ProductResult"));
const Journal         = lazy(() => import("./pages/Journal"));
const Profile         = lazy(() => import("./pages/Profile"));
const LogReaction     = lazy(() => import("./pages/LogReaction"));
const WowMoment       = lazy(() => import("./pages/WowMoment"));
const InsightDetail   = lazy(() => import("./pages/InsightDetail"));
const ProSuccess      = lazy(() => import("./pages/ProSuccess"));
const ScanIngredients = lazy(() => import("./pages/ScanIngredients"));
const SkinCheckin     = lazy(() => import("./pages/SkinCheckin"));
const Auth            = lazy(() => import("./pages/Auth"));
const Suspects        = lazy(() => import("./pages/Suspects"));
const ResetPassword   = lazy(() => import("./pages/ResetPassword"));
const ProductDetail   = lazy(() => import("./pages/ProductDetail"));
const SafeAlternatives = lazy(() => import("./pages/SafeAlternatives"));

function RequireOnboarding({ children }) {
  const location = useLocation();
  const done = storage.isOnboardingComplete();
  // /auth must be reachable pre-onboarding too — a brand-new user can sign
  // up straight from the welcome screen without finishing the 1-question flow.
  const exempt = location.pathname === "/onboarding" || location.pathname === "/auth" || location.pathname === "/reset-password";
  if (!done && !exempt) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

// Re-fetch Pro status from the server on every route change so paywalls
// surface fresh server-authoritative state regardless of localStorage drift.
function RouteProRefresher() {
  const location = useLocation();
  const { refreshPro } = useProContext();
  useEffect(() => { refreshPro?.().catch(() => {}); }, [location.pathname, refreshPro]);
  return null;
}

function RouteFallback() {
  return (
    <div
      style={{ minHeight: "100vh", padding: 20, background: "var(--sm-cream)" }}
      data-testid="route-suspense-fallback"
    >
      <SkeletonList count={3} />
    </div>
  );
}

function App() {
  useEffect(() => {
    bootSync().catch(() => { /* offline-first, silent */ });
    const replay = () => { bootSync().catch(() => {}); };
    window.addEventListener("online", replay);
    track(EVENTS.APP_OPENED);
    notifications.startScheduler();
    return () => window.removeEventListener("online", replay);
  }, []);

  return (
    <div className="App">
      <ProProvider>
        <BrowserRouter>
          <RouteProRefresher />
          <OfflineBadge />
          <InstallPrompt />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/"             element={<RequireOnboarding><Home /></RequireOnboarding>} />
              <Route path="/scan"         element={<RequireOnboarding><ScanEntry /></RequireOnboarding>} />
              <Route path="/scan/result"  element={<RequireOnboarding><ProductResult /></RequireOnboarding>} />
              <Route path="/scan/barcode" element={<RequireOnboarding><Scan /></RequireOnboarding>} />
              <Route path="/scan/photo"   element={<RequireOnboarding><ScanIngredients /></RequireOnboarding>} />
              <Route path="/scan/photo/:productId" element={<RequireOnboarding><ScanIngredients /></RequireOnboarding>} />
              <Route path="/scan/manual"  element={<RequireOnboarding><ManualEntry /></RequireOnboarding>} />
              <Route path="/journal"      element={<RequireOnboarding><Journal /></RequireOnboarding>} />
              <Route path="/profile"      element={<RequireOnboarding><Profile /></RequireOnboarding>} />
              <Route path="/log-reaction/:productId" element={<RequireOnboarding><LogReaction /></RequireOnboarding>} />
              <Route path="/wow-moment"   element={<RequireOnboarding><WowMoment /></RequireOnboarding>} />
              <Route path="/insight-detail/:id" element={<RequireOnboarding><InsightDetail /></RequireOnboarding>} />
              <Route path="/pro-success"  element={<RequireOnboarding><ProSuccess /></RequireOnboarding>} />
              <Route path="/scan-ingredients/:productId" element={<RequireOnboarding><ScanIngredients /></RequireOnboarding>} />
              <Route path="/skin-checkin"  element={<RequireOnboarding><SkinCheckin /></RequireOnboarding>} />
              <Route path="/auth"         element={<RequireOnboarding><Auth /></RequireOnboarding>} />
              <Route path="/reset-password" element={<RequireOnboarding><ResetPassword /></RequireOnboarding>} />
              <Route path="/suspects"     element={<RequireOnboarding><Suspects /></RequireOnboarding>} />
              <Route path="/journal/product/:id" element={<RequireOnboarding><ProductDetail /></RequireOnboarding>} />
              <Route path="/safe-alternatives" element={<RequireOnboarding><SafeAlternatives /></RequireOnboarding>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ProProvider>
    </div>
  );
}

export default App;
